import { NumericStepper, Box, Flex, Stack, Text } from "flicket-ui";
import { Badge } from "~components";
import { FixedRatioImage } from "~components/common/Image";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import Price from "~components/common/Price";
import PriceWithFee from "~components/reservation/common/common.PriceWithFee";
import { useRouter } from "next/router";
import noop from "lodash/noop";
import ListItemDetails from "./NonSeatedListItem.Details";

interface INonSeatedListItem {
  ticket: IReleaseTicket;
  onChange: (value: number, ticketId: string, zoneId: string) => void;
  quantity: number;
  showHiddenFees: boolean;
  showHighDemandBadge: boolean;
  onClick?: () => void;
}

export const ExperienceSalesTicketTypeItem = ({
  ticket,
  onChange,
  quantity,
  showHiddenFees,
  showHighDemandBadge,
}: INonSeatedListItem) => {
  const router = useRouter();
  // Will be used to check if the ticket has the enhanced experience view
  const showEnhanced = ticket.description || ticket.imageGallery?.length > 0;

  function onClick() {
    void router.push({
      pathname: `/events/[eventId]/ticket/[ticketId]`,
      query: {
        eventId: router.query.eventId,
        ticketId: ticket.id,
      },
    });
  }
  return (
    <>
      {!showEnhanced && (
        <CardItemBasic
          ticket={ticket}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
        />
      )}

      {showEnhanced && (
        <CardItemEnhanced
          ticket={ticket}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
        />
      )}
    </>
  );
};

function CardItemEnhanced({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
  onClick = noop,
}: INonSeatedListItem) {
  return (
    <Box id={ticket.id} boxShadow="card" mb={3} borderRadius="md">
      <Flex
        alignItems="center"
        p={2}
        flex={1}
        borderRadius="md"
        cursor={onClick ? "pointer" : "auto"}
        // @ts-expect-error will turn into a styld component
        boxShadow={quantity > 0 ? "0 0 0 3px #444" : "none"}
      >
        <Stack
          gap={2}
          direction={["vertical", "vertical", "horizontal"]}
          justifyContent="space-between"
          flex={1}
        >
          {ticket.imageGallery?.[0] && (
            <Box onClick={onClick}>
              <FixedRatioImage
                src={ticket.imageGallery?.[0].original.src}
                alt={ticket.name}
                ratio={"196 / 130"}
                width={{ _: "100%", xs: 196, sm: 260 }}
                borderRadius="sm"
              />
            </Box>
          )}
          <Box flex={1}>
            <Stack
              gap={3}
              direction="horizontal"
              justifyContent="space-between"
            >
              <Box flex={1}>
                <Text variant="header.S">{ticket.name}</Text>
                <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
                <ListItemDetails ticketType={ticket} />
              </Box>
              <Box>
                <NumericStepper
                  value={quantity}
                  onChange={(value: number) =>
                    onChange(value, ticket.id, ticket.zoneId)
                  }
                  min={ticket.minPurchaseQuantity}
                  max={ticket.maxPurchaseQuantity}
                />
              </Box>
            </Stack>
            {showHighDemandBadge && <Badge>In High demand</Badge>}
            {ticket.description && (
              <Text variant="regular">{ticket.description}</Text>
            )}
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
}

function CardItemBasic({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
  onClick,
}: INonSeatedListItem) {
  return (
    <Box mb={3} id={ticket.id} boxShadow="card" borderRadius="md">
      <Box
        alignItems="center"
        p={2}
        onClick={onClick}
        cursor={onClick ? "pointer" : "auto"}
        borderRadius="md"
        // @ts-expect-error will turn into a styld component
        boxShadow={quantity > 0 ? "0 0 0 3px #444" : "none"}
      >
        <Stack gap={3} direction="horizontal" justifyContent="space-between">
          <Box flex={1}>
            <Text variant="header.S">{ticket.name}</Text>
            <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
            <ListItemDetails ticketType={ticket} />
            {showHighDemandBadge && <Badge>In High demand</Badge>}
          </Box>
          <Box>
            <NumericStepper
              value={quantity}
              onChange={(value: number) =>
                onChange(value, ticket.id, ticket.zoneId)
              }
              min={ticket.minPurchaseQuantity}
              max={ticket.maxPurchaseQuantity}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function TicketPriceAndFee({
  ticket,
  showFees,
}: {
  showFees: boolean;
  ticket: IReleaseTicket;
}) {
  return (
    <Box>
      <Text as="span" variant="regular">
        <Price price={ticket.price} />
      </Text>
      {showFees && (
        <Text as="span" variant="small">
          {" "}
          <PriceWithFee.Fee showFees={true} ticket={ticket} />
        </Text>
      )}
    </Box>
  );
}
